import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Save, Plus, Trash2, Search, Upload } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { Page } from "@/components/Page";

export function AdminGIItems() {
  const [items, setItems] = useState([]);
  const [newItem, setNewItem] = useState("");
  const [busy, setBusy] = useState(false);
  const [q, setQ] = useState("");

  const load = () => api.get("/gi-items")
    .then(r => setItems(r.data?.items || []))
    .catch(err => toast.error(formatError(err)));
  useEffect(() => { load(); }, []);

  const add = () => {
    const v = newItem.trim();
    if (!v) return;
    if (items.some(x => x.toLowerCase() === v.toLowerCase())) { toast.error("Already in the list"); return; }
    setItems([v, ...items]); setNewItem("");
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const edit = (i, v) => setItems(items.map((x, idx) => idx === i ? v : x));

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/admin/gi-items", { items });
      toast.success(r.data?.message || "Saved");
      load();
    } catch (err) { toast.error(formatError(err)); }
    finally { setBusy(false); }
  };

  const importCSV = async (e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file) return;
    const text = await file.text();
    const parsed = text.split(/\r?\n/).map(x => x.trim()).filter(Boolean);
    const merged = [...items];
    const lower = new Set(merged.map(x => x.toLowerCase()));
    let added = 0;
    for (const p of parsed) {
      if (!lower.has(p.toLowerCase())) { merged.push(p); lower.add(p.toLowerCase()); added += 1; }
    }
    setItems(merged);
    toast.success(`Merged ${added} new item${added === 1 ? "" : "s"}. Click Save to publish.`);
  };

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();
    if (!query) return items;
    return items.filter(x => x.toLowerCase().includes(query));
  }, [items, q]);

  return (
    <Page title="GI items catalog" subtitle="Master list used by GI 3A form. Users can search and select from this list; add or remove items as new GIs are registered.">
      <div className="admin-toolbar" data-testid="gi-items-toolbar">
        <div className="gi-select-input" style={{ maxWidth: 320 }}>
          <Search size={14} />
          <input value={q} onChange={e => setQ(e.target.value)} placeholder="Filter…" data-testid="gi-items-filter" />
        </div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <label className="secondary-btn" data-testid="gi-items-import">
            <Upload size={13} /> Import CSV / TXT
            <input type="file" accept=".csv,.txt" onChange={importCSV} style={{ display: "none" }} data-testid="gi-items-import-input" />
          </label>
          <button className="primary-btn" onClick={save} disabled={busy} data-testid="gi-items-save">{busy ? "Saving…" : <>Save catalog <Save size={14} /></>}</button>
        </div>
      </div>

      <div className="form-section" style={{ display: "grid", gridTemplateColumns: "1fr auto", gap: 8, marginBottom: 12 }}>
        <input value={newItem} onChange={e => setNewItem(e.target.value)} onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); add(); } }} placeholder="Add a new GI item (e.g., Bhagalpur Silk)" data-testid="gi-items-new-input" />
        <button className="primary-btn" onClick={add} data-testid="gi-items-add"><Plus size={13} /> Add</button>
      </div>

      <div className="muted" style={{ marginBottom: 8 }} data-testid="gi-items-count">Total: {items.length} · Showing: {filtered.length}</div>
      <div className="admin-table" data-testid="gi-items-list">
        {filtered.map((v, i) => {
          const idx = items.indexOf(v);
          return (
            <div className="admin-row" key={`${v}-${i}`} data-testid={`gi-item-${i}`}>
              <input value={v} onChange={e => edit(idx, e.target.value)} data-testid={`gi-item-input-${i}`} />
              <button type="button" className="danger-btn" onClick={() => remove(idx)} data-testid={`gi-item-delete-${i}`}><Trash2 size={12} /></button>
            </div>
          );
        })}
      </div>
    </Page>
  );
}
